import { zustandStorage } from "@helpers/storage";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";

export type ModuleSession = {
	numericId: number;
	trackedAt?: number;
};

type State = {
	sessionByModule: Record<string, ModuleSession>;
	resumeIndexBySession: Record<string, number>;
	setModuleSession: (moduleId: string, session: { numericId: number }) => void;
	clearModuleSession: (moduleId: string) => void;
	clearModuleSessions: (moduleIds: string[]) => void;
	getResumeSessionIndex: (
		sessionId: number,
		itemCount: number,
	) => number | undefined;
	setResumeSessionIndex: (sessionId: number, index: number) => void;
	clearResumeSessionIndex: (sessionId: number) => void;
	// Pre-test completion (submitted, any score) and post-test pass (>= 80%) drive
	// review-unlock and, under the live flow, the module/post-test gating model.
	preTestCompleted: boolean;
	postTestPassed: boolean;
	setPreTestCompleted: (completed: boolean) => void;
	setPostTestPassed: (passed: boolean) => void;
};

export const useProethicaSessionStore = create<State>()(
	persist(
		(set, get) => ({
			sessionByModule: {},
			resumeIndexBySession: {},
			preTestCompleted: false,
			postTestPassed: false,
			getResumeSessionIndex: (sessionId, itemCount) => {
				if (itemCount <= 0) {
					return undefined;
				}

				const stored = get().resumeIndexBySession[String(sessionId)];
				if (stored == null || Number.isNaN(stored)) {
					return undefined;
				}

				return Math.min(Math.max(0, Math.floor(stored)), itemCount - 1);
			},
			setResumeSessionIndex: (sessionId, index) =>
				set((state) => ({
					resumeIndexBySession: {
						...state.resumeIndexBySession,
						[String(sessionId)]: index,
					},
				})),
			clearResumeSessionIndex: (sessionId) =>
				set((state) => {
					const next = { ...state.resumeIndexBySession };
					delete next[String(sessionId)];
					return { resumeIndexBySession: next };
				}),
			setModuleSession: (moduleId, session) =>
				set((state) => ({
					sessionByModule: {
						...state.sessionByModule,
						[moduleId]: { numericId: session.numericId, trackedAt: Date.now() },
					},
				})),
			clearModuleSession: (moduleId) =>
				set((state) => {
					const next = { ...state.sessionByModule };
					delete next[moduleId];
					return { sessionByModule: next };
				}),
			clearModuleSessions: (moduleIds) => {
				if (moduleIds.length === 0) {
					return;
				}
				set((state) => {
					const next = { ...state.sessionByModule };
					let changed = false;
					for (const moduleId of moduleIds) {
						changed ||= moduleId in next;
						delete next[moduleId];
					}
					if (!changed) {
						return state;
					}
					return { sessionByModule: next };
				});
			},
			setPreTestCompleted: (preTestCompleted) => set({ preTestCompleted }),
			setPostTestPassed: (postTestPassed) => set({ postTestPassed }),
		}),
		{
			name: "proethicaSessionStore",
			storage: createJSONStorage(() => zustandStorage),
			partialize: (state) => ({
				sessionByModule: state.sessionByModule,
				resumeIndexBySession: state.resumeIndexBySession,
			}),
		},
	),
);
